<script lang="ts">
  import { HttpPromise, HttpPromiseWithEntireResponse } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import { debounce } from "lodash";
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import Pagination from "@shared/svelte/Components/Pagination/Small.svelte";
  import type { Distributor } from "@shared/types/Distributor";
  import type { PaginationDetails } from "@shared/types/Pagination";

  export let distributor: Distributor;
  export let keepPrefilled: boolean;
  export let addProduct;
  export let addProductSuccessCb;
  export let showAddProductModal;
  export let mobileMode: boolean;

  var ADD_PRODUCTS_PER_PAGE = 10;

  const addProduct_goToPage = function (page) {
    pagination.page = page;
    addProductSearch();
  };
  let pagination: PaginationDetails = {
    perPage: ADD_PRODUCTS_PER_PAGE,
    page: 1,
    totalEntries: 0,
    goToPage: addProduct_goToPage,
  };

  let addProductResults = [];
  let addProductSearchPending = false;
  const addProductSearch = async function () {
    addProductSearchPending = true;
    try {
      const res = await HttpPromiseWithEntireResponse({
        method: "GET",
        url: "/api/retailer/distributors/" + distributor.id + "/products",
        data: {
          search: addProduct.filter,
          offset: (pagination.page - 1) * ADD_PRODUCTS_PER_PAGE,
        },
      });
      if (res.ok) {
        let data = await res.json();

        addProductSearchPending = false;
        addProductResults = data;
        pagination.totalEntries = parseInt(res.headers.get("X-Pagination-Entries"));

        if (keepPrefilled) {
          keepPrefilled = false;
        }
      }
    } catch (e) {
      console.error(e);
      addProductSearchPending = false;
      Flash.now({
        type: "error",
        header: "Error",
        message: "There was an error retrieving products.",
      });
    }
  };
  addProductSearch();
  const debouncedAddProductSearch = debounce(addProductSearch, 250);

  let addProductPending: boolean = false;
  const doAddProduct = async function (product) {
    addProductPending = true;
    try {
      const res = await HttpPromise({
        method: "POST",
        url: "/api/retailer/custom_prices",
        data: {
          custom_price: {
            product_id: product.id,
            unit_price: product.unit_price || 0,
            case_price: product.case_price || 0,
            distributor_id: distributor.id,
          },
        },
      });
      addProductPending = false;
      addProductSuccessCb(res, product);
    } catch (e) {
      addProductPending = false;
      Flash.now({
        type: "error",
        header: "Error",
        message: "There was an error adding custom pricing for this product.",
      });
    }
  };
  const closeAddProductModal = function () {
    showAddProductModal = false;
  };

</script>

<Modal bind:show={showAddProductModal}>
  <div
    class="settings__editable-row-add-product-modal background-white padding--small padding--2--md"
  >
    <div class="padding-bottom--small">
      <span class="s4">Select a product to define its custom pricing</span>
      <span class="float-right s4 cursor-pointer" on:click={closeAddProductModal}>&times;</span>
    </div>

    <div class="input-wrapper full-width margin-bottom--small">
      <input
        class="input"
        type="text"
        bind:value={addProduct.filter}
        on:input={() => {
          pagination.page = 1;
          debouncedAddProductSearch();
        }}
        placeholder="Search products..."
      />
    </div>

    <div class="padding-left--default padding-right--default padding-bottom--smaller show--md">
      <div class="grid-row opacity-60">
        <div class="grid-col c12-12 c6-12--md">
          <div class="s6">Product</div>
        </div>
        <div class="grid-col c2-12">
          <div class="s6">Unit Price</div>
        </div>
        <div class="grid-col c2-12">
          <div class="s6">Case Price</div>
        </div>
        <div class="grid-col c2-12" />
      </div>
    </div>

    <div class="tile pricing-details-tile">
      {#if !addProductSearchPending}
        {#each addProductResults as product}
          <div
            class="padding--smaller padding--1--md tile__row"
            id="pricing_productrow_{product.id}"
          >
            <div class="grid-row">
              <div class="grid-col c9-12 c6-12--md">
                <div class="s6 ellipsis notranslate">
                  {product.name}
                  <span class="opacity-60"
                    >— {product.container_size}
                    {product.container_units}
                    {product.container_type},
                    {#if product.container_carton_size}
                      <span>{product.container_carton_size} pack,</span>{/if}
                    {#if product.container_case_size}<span>{product.container_case_size}/case</span
                      >{/if}</span
                  >
                </div>
              </div>
              {#if !mobileMode}
                <div class="grid-col c2-12">
                  <div class="s6 ellipsis">
                    {#if product.unit_price}
                      <span>${product.unit_price.toFixed(2)}</span>
                    {:else}
                      <span class="opacity-50">None Set</span>
                    {/if}
                  </div>
                </div>

                <div class="grid-col c2-12">
                  <div class="s6 ellipsis">
                    {#if product.case_price}
                      <span>${product.case_price.toFixed(2)}</span>
                    {:else}
                      <span class="opacity-50">None Set</span>
                    {/if}
                  </div>
                </div>
              {/if}
              <div class="grid-col c3-12 c2-12--md align-right">
                <button
                  class="button button--label button--primary"
                  on:click={() => doAddProduct(product)}>Select</button
                >
              </div>
            </div>
          </div>
        {/each}
      {/if}
      {#if !addProductSearchPending && addProductResults.length === 0}
        <div class="h3 align-center padding--default opacity-50">No products found.</div>
      {/if}
      {#if addProductSearchPending}
        <div class="h3 align-center padding--default opacity-50">Loading...</div>
      {/if}
    </div>

    <div class="margin-top--default fizz-styles align-center">
      <Pagination {pagination} />
    </div>
  </div>
</Modal>
